import type { IUser } from "@/api";
import { DataTable } from "@/components/shared/DataTable";
import { useAuth } from "@/contexts/auth/useAuth";
import type {
    OnChangeFn,
    PaginationState,
    RowSelectionState,
    SortingState,
} from "@tanstack/react-table";
import { AlertCircle, SearchX, Users } from "lucide-react";

import { columns } from "./columns";

export interface UserTableProps {
    rows: IUser[];
    rowCount: number;
    isLoading: boolean;
    isError: boolean;
    isRecoveringPage: boolean;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
    onRetry: () => void;
    pagination: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
    rowSelection: RowSelectionState;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
}

const iconClass = "text-muted-foreground/50 size-10";

export function UserTable({
    rows,
    rowCount,
    isLoading,
    isError,
    isRecoveringPage,
    hasActiveFilters,
    onClearFilters,
    onRetry,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    rowSelection,
    onRowSelectionChange,
}: UserTableProps) {
    const { user: currentUser } = useAuth();

    return (
        <DataTable
            data={rows}
            columns={columns}
            entityLabel="users"
            rowCount={rowCount}
            isLoading={isLoading}
            isError={isError}
            isRecovering={isRecoveringPage}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={onClearFilters}
            onRetry={onRetry}
            pagination={pagination}
            onPaginationChange={onPaginationChange}
            sorting={sorting}
            onSortingChange={onSortingChange}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            getRowId={(row) => String(row.id)}
            enableMultiRowSelection
            // You can't bulk-delete yourself any more than you can row-delete
            // yourself; select-all skips your own row too.
            enableRowSelection={(row) => row.original.id !== currentUser?.id}
            errorState={{
                icon: <AlertCircle className={iconClass} />,
                title: "Couldn't load users",
                body: "Something went wrong. Please try again.",
            }}
            recoveringState={{
                icon: <Users className={iconClass} />,
                title: "Loading users",
                body: "Moving to the last available page.",
            }}
            filteredState={{
                icon: <SearchX className={iconClass} />,
                title: "No users match your filters",
                body: "Try adjusting your search or filters.",
            }}
            emptyState={{
                icon: <Users className={iconClass} />,
                title: "No users yet",
                body: "Add a user to get started.",
            }}
        />
    );
}
